import { ts } from '@/plugins/i18nFormat'
import { localization } from '@/plugins/i18n'
import EventBoat from '@model/EventBoat'
import EventClass from '@model/EventClass'
import { Model, useRepo } from 'pinia-orm'
import { BelongsTo, Num, Str, Uid } from 'pinia-orm/decorators'

export default class EventCategory extends Model {
    static entity = 'events-categories'

    @Uid()
    declare id: string
    @Uid()
    declare eventId: string // only for sharding
    @Uid()
    declare eventClassId: string
    @BelongsTo(() => EventClass, 'eventClassId')
    declare eventClass: EventClass
    @Str(null)
    declare title: string
    @Str(null)
    declare shortcut: string
    @Str(null)
    declare sex: 'male' | 'female' | 'mix' | 'male+mix' | 'female+mix' | null
    @Num(null)
    declare ageUnder: number
    @Num(null)
    declare ageAbove: number
    @Str(null)
    declare otherLimit: 'all' | 'cze' | 'region' | 'district' | null

    get dump() {
        return {
            id: this.id,
            title: this.title,
            shortcut: this.shortcut,
            sex: this.sex,
            ageUnder: this.ageUnder,
            ageAbove: this.ageAbove,
            otherLimit: this.otherLimit,
        }
    }

    get visibleAge() {
        return (this.ageAbove !== null && this.ageUnder !== null)
            ? ts('bothAge', { under: this.ageUnder, above: this.ageAbove })
            : (this.ageAbove !== null
                ? ts('aboveAge', { age: this.ageAbove })
                : (this.ageUnder !== null
                    ? ts('underAge', { age: this.ageUnder })
                    : ts('noLimits')))
    }

    get visibleSex() {
        return ts(this.sex === null ? 'sexLimit-all' : 'sexLimit-' + this.sex)
    }

    get visibleShortcut() {
        const locale = localization.global.locale.value as 'cs' | 'en'

        return this.visibleShortcutLocale(locale)
    }

    get fileName() {
        if (this.shortcut === null || this.shortcut === undefined)
            return this.visibleShortcut.trim().replaceAll(' ', '-')

        return this.shortcut.toAscii().replaceAll(' ', '-')
    }

    get explanation() {
        return (this.sex === null ? ts('sexLimit-none') : this.visibleSex) + ', ' + this.visibleAge + ', '
            + this.visibleOtherLimit
    }

    get visibleTitle() {
        const locale = localization.global.locale.value as 'cs' | 'en'

        return this.visibleTitleLocale(locale)
    }

    visibleShortcutLocale(locale: 'cs' | 'en') {
        if (this.shortcut === null || this.shortcut === undefined) {
            let generic = ''

            if (this.ageUnder !== null)
                generic += 'U' + (this.ageUnder + 1)
            else if (this.ageAbove !== null)
                generic += 'O' + this.ageAbove

            if (this.sex !== null)
                generic += ts('categoryShortcut-' + this.sex, {}, { locale: locale })

            if (this.otherLimit !== null)
                generic += ts('categoryShortcut-' + this.otherLimit, {}, { locale: locale })

            return generic.trim()
        }

        return this.shortcut
    }

    visibleTitleLocale(locale: 'cs' | 'en') {
        if (this.title === null || this.title === undefined) {
            let generic = ts('categoryFull-' + (this.otherLimit === null ? 'all' : this.otherLimit), {}, {
                locale: locale,
            })

            if (this.sex !== null)
                generic += ' ' + ts('categoryFull-' + this.sex, {}, { locale: locale })

            if (this.ageAbove !== null)
                generic += ' ' + ts('aboveAge', { age: this.ageAbove }, { locale: locale })

            if (this.ageUnder !== null)
                generic += ' ' + ts('underAge', { age: this.ageUnder }, { locale: locale })

            return generic.trim()
        }

        return this.title
    }

    get visibleShortcutCz() {
        return this.visibleShortcutLocale('cs')
    }

    get visibleShortcutEn() {
        return this.visibleShortcutLocale('en')
    }

    get visibleTitleCz() {
        return this.visibleTitleLocale('cs')
    }

    get visibleTitleEn() {
        return this.visibleTitleLocale('en')
    }

    get visibleOtherLimit() {
        return ts(this.otherLimit === null ? 'otherLimit-all' : 'otherLimit-' + this.otherLimit)
    }

    get boats() {
        const boats = useRepo(EventBoat)
            .where('eventId', this.eventClass.eventId)
            .where('boatClassId', this.eventClass.boatClassId)
            .with('manualCategories')
            .with('crew', (qr) => {
                qr.with('member', (qrm) => {
                    qrm.with('club')
                })
            })
            .get() as EventBoat[]

        let count = 0

        for (const b of boats) {
            if (this.isBoatInCategory(b))
                count++
        }

        return count
    }

    isBoatInCategory(boat: EventBoat): boolean {
        if (boat.overrideCategories)
            return boat.manualCategories.some(boatCategory => boatCategory.eventCategoryId === this.id)

        if (this.sex !== null) {
            const boatSex = boat.sex

            if (boatSex === 'male' && (this.sex === 'female' || this.sex === 'mix' || this.sex === 'female+mix'))
                return false

            if (boatSex === 'female' && (this.sex === 'male' || this.sex === 'mix' || this.sex === 'male+mix'))
                return false

            if (boatSex === 'mix' && (this.sex === 'male' || this.sex === 'female'))
                return false
        }

        if (this.ageUnder !== null && boat.effectiveAgeOldest > this.ageUnder)
            return false

        if (this.ageAbove !== null && boat.effectiveAgeYoungest < this.ageAbove)
            return false

        if (this.otherLimit === 'cze' && !boat.isCzech)
            return false

        let eventClass = this.eventClass

        if (eventClass === undefined)
            eventClass = useRepo(EventClass).with('event').find(this.eventClassId)

        if (
            this.otherLimit === 'region' && eventClass.event.clubId !== undefined
            && !boat.isFromRegion(eventClass.event.clubId)
        )
            return false

        if (
            this.otherLimit === 'district' && eventClass.event.clubId !== undefined
            && !boat.isFromDistrict(eventClass.event.clubId)
        )
            return false

        return true
    }

    static piniaOptions = {
        persist: false,
    }
}
